import { useEffect, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { List, X } from '@phosphor-icons/react';

const NAV_ITEMS = [
  { id: 'home', key: 'home' },
  { id: 'capabilities', key: 'capabilities' },
  { id: 'research', key: 'research' },
  { id: 'partnerships', key: 'partnerships' },
  { id: 'contact', key: 'contact' },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Navbar() {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid={TID.nav.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0A1128]/85 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <button
          data-testid={TID.nav.logo}
          onClick={() => handleClick('home')}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 flex items-center justify-center border border-white/20 group-hover:border-[#4F6355] transition-colors">
            <span className="font-display font-black text-[#F8FAFC] text-sm tracking-tighter">C</span>
          </span>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-white text-sm tracking-tight uppercase">CDPA</span>
            <span className="font-mono text-[10px] tracking-[0.25em] text-[#94A3B8] uppercase mt-0.5">Systems Concept</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={TID.nav[item.key]}
              onClick={() => handleClick(item.id)}
              className="text-[12px] tracking-[0.2em] uppercase font-semibold text-[#94A3B8] hover:text-white transition-colors"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={TID.nav.langToggle}
            onClick={toggle}
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-[#4F6355] hover:bg-white/5 transition-all text-[11px] tracking-[0.2em] uppercase font-bold text-white"
            aria-label="Toggle language"
          >
            <span className={lang === 'en' ? 'text-white' : 'text-[#94A3B8]'}>EN</span>
            <span className="text-[#3E4C5E]">/</span>
            <span className={lang === 'ro' ? 'text-white' : 'text-[#94A3B8]'}>RO</span>
          </button>

          <button
            data-testid={TID.nav.cta}
            onClick={() => handleClick('contact')}
            className="hidden md:inline-flex btn-primary px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold"
          >
            {t.cta.contactUs}
          </button>

          <button
            data-testid={TID.nav.mobileToggle}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/15 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid={TID.nav.mobileMenu}
          className="lg:hidden bg-[#0A1128]/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleClick(item.id)}
                className="text-left px-3 py-3 text-sm tracking-[0.18em] uppercase font-semibold text-[#F8FAFC] hover:bg-white/5 transition-colors"
              >
                {t.nav[item.key]}
              </button>
            ))}
            <button
              data-testid="nav-mobile-language-toggle"
              onClick={toggle}
              className="mt-3 self-start px-3 py-2 border border-white/15 text-[11px] tracking-[0.2em] uppercase font-bold text-white"
            >
              <span className={lang === 'en' ? 'text-white' : 'text-[#94A3B8]'}>EN</span>
              <span className="text-[#3E4C5E] mx-2">/</span>
              <span className={lang === 'ro' ? 'text-white' : 'text-[#94A3B8]'}>RO</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export { scrollToId };
